import type { CinematicOverrides } from '../../../renderer/src/cinematic-sequence';
import type { TimedCinematicWaypoint } from './cinematic-timeline';
import { filmTraversalBounds } from './camera-plan';

export const SCENE_PROPERTIES = ['mapStyle', 'cameraStyle', 'cameraDistance', 'cameraGimbal', 'cameraAngle', 'cameraZoom', 'depthOfField', 'presentationAppearance'] as const satisfies readonly (keyof CinematicOverrides)[];
export type SceneProperty = typeof SCENE_PROPERTIES[number];

/** Each property clips its transition against neighbors on its own track. */
export function cinematicPropertyTimeline(timeline: readonly TimedCinematicWaypoint[], property: SceneProperty, duration: number): TimedCinematicWaypoint[] {
  const keys = timeline.filter(item => item.waypoint.overrides[property] !== undefined);
  return keys.map((item, index) => {
    const transition = item.waypoint.transition;
    const arrival = transition.timing === 'arrival';
    const gap = arrival ? item.seconds - (keys[index - 1]?.seconds ?? 0)
      : (keys[index + 1]?.seconds ?? (item.waypoint.filmProgress === undefined ? filmTraversalBounds(duration).endSeconds : duration)) - item.seconds;
    const effectiveDurationSeconds = transition.type === 'cut' ? 0
      : Math.max(0, Math.min(transition.entireInterval ? gap : transition.durationSeconds, gap));
    return { ...item, effectiveDurationSeconds, transitionStartSeconds: arrival ? item.seconds - effectiveDurationSeconds : item.seconds };
  });
}

/** Preserve bundled transitions when their property windows still coincide. */
export function cinematicPropertyGroups(timeline: readonly TimedCinematicWaypoint[], duration: number) {
  const groups = new Map<string, TimedCinematicWaypoint>();
  for (const property of SCENE_PROPERTIES) for (const item of cinematicPropertyTimeline(timeline, property, duration)) {
    const id = `${item.waypoint.id}:${item.transitionStartSeconds}:${item.effectiveDurationSeconds}`;
    const previous = groups.get(id);
    groups.set(id, { ...item, waypoint: { ...item.waypoint,
      overrides: { ...previous?.waypoint.overrides, [property]: item.waypoint.overrides[property] } } });
  }
  return [...groups.values()].sort((a, b) => a.seconds - b.seconds);
}
